import { EventType, InteractionStatus } from '@azure/msal-browser';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { type ReactNode, useEffect, useState } from 'react';
import { extractAadstsCode, trackAuthEvent } from '../observability/authTelemetry';
import { loginRequest, msalInstance } from './msalConfig';
import { TeamsSsoProvider, useTeamsSso } from './TeamsSsoContext';
import { signInViaTeams } from './teamsSignin';

export function AuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    void msalInstance.initialize().then(() => msalInstance.handleRedirectPromise());

    const callbackId = msalInstance.addEventCallback((message) => {
      if (
        (message.eventType === EventType.LOGIN_SUCCESS ||
          message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        message.payload &&
        'account' in message.payload &&
        message.payload.account
      ) {
        msalInstance.setActiveAccount(message.payload.account);
      }
    });

    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <TeamsSsoProvider>{children}</TeamsSsoProvider>
    </MsalProvider>
  );
}

// Common Entra error codes mapped to Finnish user-facing copy. Keep these
// short; the error box is narrow. Unknown AADSTS-NNNNN codes fall through to
// the raw message so operators still have something to grep in support tickets.
const AADSTS_FI_MESSAGES: Record<string, string> = {
  AADSTS65001:
    'Järjestelmänvalvojan hyväksyntä puuttuu. Pyydä IT:tä antamaan sovellukselle hyväksyntä (admin consent).',
  AADSTS50020: 'Tilisi ei kuulu tähän vuokraajaan. Varmista, että kirjaudut oikealla työtunnuksella.',
  AADSTS500011:
    'Sovellusta ei ole rekisteröity tähän vuokraajaan. Tarkista Entra-rekisteröinti IT:n kanssa.',
  AADSTS50076:
    'Monivaiheinen tunnistautuminen (MFA) vaaditaan. Yritä uudelleen ja suorita MFA loppuun.',
  AADSTS700016:
    'Sovellusta ei löydy. Entra-rekisteröinti on saattanut poistua — yhteys IT:hen.',
  AADSTS70011: 'Pyydetty oikeus (scope) ei ole voimassa. Yhteys Claritoon jos toistuu.',
};

function translateError(raw: string | null): string | null {
  if (!raw) return null;
  const code = extractAadstsCode(raw);
  if (code && AADSTS_FI_MESSAGES[code]) {
    return `${AADSTS_FI_MESSAGES[code]} (${code})`;
  }
  return raw;
}

function SignInPrompt({ inTeams }: { inTeams: boolean }) {
  const { instance, inProgress } = useMsal();
  const busy = inProgress !== InteractionStatus.None;
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = async () => {
    setError(null);
    const started = Date.now();
    try {
      if (inTeams) {
        trackAuthEvent('auth.popup_fallback', { inTeams: true });
        const origin = window.location.origin;
        // Kick off the Teams popup in the background. Teams' success callback
        // is unreliable after the cross-origin redirect to MS login, so we
        // don't await this promise; we watch MSAL's account store instead.
        signInViaTeams(`${origin}/?teams-popup=1`).catch((err) => {
          console.warn('signInViaTeams settled with error:', err);
        });

        // Poll for MSAL sign-in completion for up to 2 minutes. The popup
        // writes tokens to localStorage (shared origin) well before any
        // notifySuccess message arrives.
        const deadline = Date.now() + 120_000;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 500));
          if (instance.getAllAccounts().length > 0) {
            window.location.reload();
            return;
          }
        }
        trackAuthEvent('auth.popup_timeout', {
          inTeams: true,
          duration_ms: Date.now() - started,
        });
        setError('Kirjautuminen aikakatkaistiin.');
      } else {
        await instance.loginRedirect(loginRequest);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('Sign-in failed:', err);
      trackAuthEvent('auth.silent_sso_failure', {
        inTeams,
        duration_ms: Date.now() - started,
        errorCode: message.slice(0, 120),
        errorSubcode: extractAadstsCode(message),
      });
      setError(message);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        padding: 40,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 56 }}>🔐</div>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)' }}>
        Kirjaudu sisään
      </h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, maxWidth: 420 }}>
        Microsoft AI — What's New edellyttää Entra-kirjautumista. Käytä työpaikan
        Microsoft-tiliäsi jatkaaksesi.
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={() => void handleSignIn()}
        style={{
          padding: '10px 22px',
          borderRadius: 8,
          border: 'none',
          background: '#0078D4',
          color: '#fff',
          fontSize: 14,
          fontWeight: 700,
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? 'Kirjaudutaan...' : error ? 'Yritä uudelleen' : 'Kirjaudu Microsoft-tilillä'}
      </button>
      {error && (
        <div
          style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 12,
            color: '#991B1B',
            maxWidth: 420,
          }}
        >
          {translateError(error)}
        </div>
      )}
    </div>
  );
}

export function AuthGuard({ children }: { children: ReactNode }) {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  // Shared with useAuth via TeamsSsoProvider — one probe per session.
  const { inTeams, ready: teamsSsoReady } = useTeamsSso();

  if (inTeams === null || teamsSsoReady === null) return null;
  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return null;
  }
  // Teams silent-SSO happy path: skip the MSAL authentication gate entirely.
  // The rest of the tree works off the Teams-synthesised account produced by
  // useAuth from the same TeamsSsoContext.
  if (inTeams && teamsSsoReady) {
    return <>{children}</>;
  }
  if (!isAuthenticated) {
    return <SignInPrompt inTeams={inTeams} />;
  }
  return <>{children}</>;
}
